import { TextField as MuiTextField, makeStyles } from "@material-ui/core";
import MuiAutocomplete, {
  type AutocompleteProps as MuiAutocompleteProps,
} from "@material-ui/lab/Autocomplete";
import type Fuse from "fuse.js";
import { useCallback } from "react";
import { defineMessage, useIntl } from "react-intl";

const MAX_RESULTS_DEFAULT = 6;

const noOptionsMessage = defineMessage({
  id: "form.input.autocomplete.noResults",
  defaultMessage: "No results",
});

// MUI Autocomplete overrides some styles of our TextField
// that causes the layout to appear different to normal TextFields
// This restores the original padding values,
// with the extra 6px left padding added by Autocomplete
const useAutocompleteStyles = makeStyles({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
      padding: "16px 0 8px 6px",
    },
  },
});

interface AutocompleteProps<T> {
  searchEngine: Fuse<T>;
  maxResults?: number;
  value?: string | null;
  onChange?: (option: T, reason: string) => void;
}

function Autocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  searchEngine,
  onChange,
  maxResults,
  ...props
}: AutocompleteProps<T> &
  Partial<
    Omit<
      MuiAutocompleteProps<
        Fuse.FuseResult<T>,
        Multiple,
        DisableClearable,
        FreeSolo
      >,
      "onChange"
    >
  >) {
  const { formatMessage } = useIntl();
  const handleChange = useCallback(
    (_, option, reason) => onChange?.(option?.item, reason),
    [onChange],
  );
  const classes = useAutocompleteStyles();
  return (
    <MuiAutocomplete
      classes={classes}
      options={[]}
      forcePopupIcon={false}
      openOnFocus={false}
      renderInput={(params) => <MuiTextField {...params} />}
      filterOptions={(_, { inputValue }) => {
        if (!inputValue) return [];
        try {
          return searchEngine.search(inputValue).splice(0, maxResults);
        } catch (error) {
          return [];
        }
      }}
      noOptionsText={formatMessage(noOptionsMessage)}
      {...props}
      onChange={handleChange}
      loading={props.loading}
    />
  );
}

Autocomplete.defaultProps = {
  maxResults: MAX_RESULTS_DEFAULT,
  onChange: undefined,
  value: null,
};

export { Autocomplete };
